import { Component, Input } from '@angular/core';
import { NavListItem, UserAccess } from '../navigation.component';
import { AuthService } from '../../../../core/services/auth.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { User } from 'anchor-shared/lib/model/entity/user';
import { DialogsService } from '../../../../core/services/dialog.service';
import { CharterStatus } from 'anchor-shared/lib/model/enum/charter-status';

@Component({
  selector: 'app-portal-navigation',
  templateUrl: './portal-navigation.component.html',
})
export class PortalNavigationComponent {
  @Input() route: string;
  @Input() user: User;
  @Input() userAccess: UserAccess;
  @Input() navProfileItems: NavListItem[];
  @Input() navLogo;
  @Input() isMobile: boolean;

  subs: Subscription[] = [];

  navItems: NavListItem[] = [
    {
      label: 'Today',
      route: '/manage/bookings-manager',
      routeParams: { activeTab: CharterStatus.approvedPending },
      condition: () => this.userAccess.isBoatOwner,
      section: 'menu',
      icon: '/prod/nav-today-icon.svg',
    },
    {
      label: 'My Charters',
      route: '/manage/bookings-manager/', // additional '/' forces this to differ from 'Today' and prevents incorrect highlighting of selected page (temporary)
      routeParams: { activeTab: CharterStatus.approvedPending },
      condition: () => this.userAccess.isBoatOwner,
      section: 'menu',
      icon: '/prod/nav-my-charters-icon.svg',
    },
    {
      label: 'Listings',
      route: '/manage/boatyard',
      condition: () => this.userAccess.isBoatOwner,
      section: 'menu',
      icon: '/prod/nav-listings-icon.svg',
    },
    {
      label: 'Messages',
      route: '/manage/messages',
      section: 'menu',
      icon: '/prod/nav-messages-icon.svg',
      condition: () =>
        this.userAccess.isBoatOwner ||
        this.userAccess.isCaptain ||
        this.userAccess.canAccessAdmin,
    },
    {
      label: 'Menu',
      customClass:
        'border border-2 border-dark rounded-5 py-2 px-3 hover-primary',
      children: [
        // ADMIN VIEW
        {
          label: 'Captain Address',
          route: '/manage/captain-address',
          condition: () => this.userAccess.isCaptain,
          section: 'dashboard',
          icon: '',
        },
        {
          label: 'Captain Description',
          route: '/manage/captain-description',
          condition: () => this.userAccess.isCaptain,
          section: 'dashboard',
          icon: '',
        },
        {
          label: 'Captain Availability',
          route: '/manage/captain-availability',
          condition: () => this.userAccess.isCaptain,
          section: 'dashboard',
          icon: '',
        },
        {
          label: 'Admin',
          route: '/manage/admin',
          condition: () => this.userAccess.isAdmin,
          section: 'dashboard',
          icon: 'build',
        },
        {
          label: 'Charter Captains',
          route: '/manage/charter-captains',
          condition: () =>
            this.userAccess.isHeadCaptain || this.userAccess.canAccessAdmin,
          section: 'dashboard',
          icon: '',
        },
        {
          label: 'Day Charters',
          route: '/manage/charters',
          condition: () => this.userAccess.canAccessAdmin,
          section: 'dashboard',
          icon: 'explore',
        },
        {
          label: 'Leads',
          route: '/manage/charters/status/lead',
          condition: () => this.userAccess.canAccessAdmin,
          subOption: true,
          section: 'dashboard',
        },
        {
          label: 'Open',
          route: '/manage/charters/status/open',
          condition: () => this.userAccess.canAccessAdmin,
          subOption: true,
          section: 'dashboard',
        },
        {
          label: 'Completed',
          route: '/manage/charters/status/completed',
          condition: () => this.userAccess.canAccessAdmin,
          subOption: true,
          section: 'dashboard',
        },
        {
          label: 'Cancelled',
          route: '/manage/charters/status/cancelled',
          condition: () => this.userAccess.canAccessAdmin,
          subOption: true,
          section: 'dashboard',
        },
        {
          label: 'Payouts',
          route: '/manage/unpaid-charters',
          condition: () => this.userAccess.canAccessAdmin,
          subOption: true,
          section: 'dashboard',
        },
        {
          label: 'Term Charters',
          route: '/manage/charters/status/term',
          condition: () => this.userAccess.canAccessAdmin,
          section: 'dashboard',
          icon: 'explore',
        },
        {
          divider: true,
          condition: () => this.userAccess.canAccessAdmin,
        },
        // BOAT OWNER / AFFILIATE
        {
          label: 'Bookings',
          route: '/manage/bookings-manager',
          routeParams: { activeTab: CharterStatus.booked },
          condition: () => this.userAccess.isBoatOwner,
          section: 'menu',
          icon: '/prod/nav-bookings-icon.svg',
        },
        {
          label: 'Paystubs',
          route: '/manage/bank-information',
          condition: () =>
            this.userAccess.isBoatOwner ||
            this.userAccess.isCaptain ||
            this.userAccess.isAffiliate ||
            this.userAccess.isCrew ||
            this.userAccess.isAdmin,
          section: 'dashboard',
          icon: '/prod/nav-payout-preferences-icon.svg',
        },
        {
          label: 'Affiliate settings',
          route: '/manage/affiliate-dashboard',
          condition: () => this.userAccess.isAffiliate,
        },
        {
          label: 'Create a new listing',
          route: '/manage/boatyard',
          condition: () => !this.userAccess.isBoatOwner,
          section: 'account',
          icon: '/prod/nav-new-listing-icon.svg',
        },
        {
          divider: true,
          condition: () => !this.userAccess.isCaptain,
        },
        {
          label: 'Boat Owner Agreement',
          route:
            'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Boat_Owner_Agreement.pdf',
          isExternal: true,
          condition: () => !this.userAccess.isCaptain,
        },
        {
          label: 'Frequently asked questions',
          route: '/boat-owner-faq',
          condition: () => !this.userAccess.isCaptain,
        },
        {
          label: 'Day charter cancellation policy',
          route:
            'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Day_Charter_Cancellation_Policy_08_10_2024.pdf',
          isExternal: true,
          condition: () => !this.userAccess.isCaptain,
        },
        {
          label: 'Term charter cancellation policy',
          route:
            'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Term_Charter_Cancellation_Policy_08_10_2024.pdf',
          isExternal: true,
          condition: () => !this.userAccess.isCaptain,
        },
      ],
    },
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private dialogsService: DialogsService,
  ) {}

  logoutClick(): void {
    const sub = this.authService.logout().subscribe(
      (data) => {
        this.router.navigate(['login']);
      },
      (error) => {
        console.log('logout failed');
      },
    );
    this.subs.push(sub);
  }

  expandNavList() {
    const navDialogSub = this.dialogsService
      .expandableNavListDialog([...this.navItems, ...this.navProfileItems])
      .subscribe();
    this.subs.push(navDialogSub);
  }
}
